import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { EmptyState } from '@enamelai/design-system'
import { Icon as SearchIcon } from '@enamelai/design-system/icons/Search'
import { toSentenceCase } from '@utils/textCase.util'
import {
  getFirstBundleReportPath,
  REPORTS_ENGAGEMENT_SUBMENU,
  REPORTS_FINANCE_SUBMENU,
  REPORTS_OPERATIONS_SUBMENU,
  REPORTS_PATIENTS_SUBMENU,
  REPORTS_PROVIDER_PERFORMANCE_SUBMENU,
  REPORTS_SIDEBAR_BUNDLE_ITEMS,
} from '@constants/Reports/reportsPage.constants'

interface ReportNavSubItem {
  id: string
  label: string
  path: string
}

interface ReportNavItem {
  id: string
  label: string
  path?: string
  items: ReportNavSubItem[]
}

const SUBMENUS_BY_BUNDLE: Record<
  string,
  ReadonlyArray<{ id: string; label: string; pathSegment: string }>
> = {
  patients: REPORTS_PATIENTS_SUBMENU,
  engagement: REPORTS_ENGAGEMENT_SUBMENU,
  operations: REPORTS_OPERATIONS_SUBMENU,
  'provider-performance': REPORTS_PROVIDER_PERFORMANCE_SUBMENU,
  finance: REPORTS_FINANCE_SUBMENU,
}

const REPORTS_NAV_ITEMS: ReportNavItem[] = [
  { id: 'bundles', label: 'Bundles', path: '/report', items: [] },
  ...REPORTS_SIDEBAR_BUNDLE_ITEMS.map((item) => ({
    id: item.id,
    label: toSentenceCase(item.label),
    items: (SUBMENUS_BY_BUNDLE[item.id] ?? []).map((submenuItem) => ({
      id: submenuItem.pathSegment,
      label: toSentenceCase(submenuItem.label),
      path: `/report/bundles/${item.id}/${submenuItem.pathSegment}`,
    })),
  })),
  {
    id: 'custom',
    label: 'Custom reports',
    path: '/report/custom',
    items: [],
  },
  {
    id: 'presets',
    label: 'Presets and automation',
    path: '/report/presets',
    items: [],
  },
]

const EXPANDABLE_NAV_IDS: string[] = REPORTS_NAV_ITEMS.filter(
  (item) => item.items.length > 0,
).map((item) => item.id)

function getActiveBundleId(pathname: string): string | undefined {
  return /^\/report\/bundles\/([^/]+)/.exec(pathname)?.[1]
}

function getActiveReportId(pathname: string): string | undefined {
  return /^\/report\/bundles\/[^/]+\/([^/]+)/.exec(pathname)?.[1]
}

function isLeafItemActive(item: ReportNavItem, pathname: string): boolean {
  if (item.items.length > 0 || !item.path) return false
  if (item.id === 'bundles') {
    return pathname === '/report' || pathname === '/report/'
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`)
}

interface ReportsSidebarProps {
  searchQuery: string
}

const ReportsSidebar = ({
  searchQuery,
}: ReportsSidebarProps): React.ReactElement => {
  const location = useLocation()
  const navigate = useNavigate()
  const activeBundleId = getActiveBundleId(location.pathname)
  const activeReportId = getActiveReportId(location.pathname)
  const [expandedItems, setExpandedItems] = useState<string[]>([])

  const filteredNavItems = useMemo((): ReportNavItem[] => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) return REPORTS_NAV_ITEMS
    return REPORTS_NAV_ITEMS.filter((item) => {
      const matchesItem = item.label.toLowerCase().includes(normalizedQuery)
      const matchesSubItem = item.items.some((subItem) =>
        subItem.label.toLowerCase().includes(normalizedQuery),
      )
      return matchesItem || matchesSubItem
    })
  }, [searchQuery])

  useEffect(() => {
    if (!activeBundleId) return
    setExpandedItems((prev) =>
      prev.includes(activeBundleId) ? prev : [...prev, activeBundleId],
    )
  }, [activeBundleId])

  useEffect(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) return
    const matchedParentIds = REPORTS_NAV_ITEMS.filter((item) =>
      item.items.some((subItem) =>
        subItem.label.toLowerCase().includes(normalizedQuery),
      ),
    ).map((item) => item.id)
    if (matchedParentIds.length === 0) return
    setExpandedItems((prev) =>
      Array.from(new Set([...prev, ...matchedParentIds])),
    )
  }, [searchQuery])

  const highlightText = (
    text: string,
    shouldHighlight: boolean,
  ): React.ReactNode => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery || !shouldHighlight) return text
    const index = text.toLowerCase().indexOf(normalizedQuery)
    if (index === -1) {
      return <span className="reports-search-highlight">{text}</span>
    }
    return (
      <>
        {text.slice(0, index)}
        <span className="reports-search-highlight">
          {text.slice(index, index + normalizedQuery.length)}
        </span>
        {text.slice(index + normalizedQuery.length)}
      </>
    )
  }

  const toggleAllItems = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    setExpandedItems(expandedItems.length > 0 ? [] : EXPANDABLE_NAV_IDS)
  }

  const toggleItem = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: ReportNavItem,
  ): void => {
    event.preventDefault()
    event.stopPropagation()
    if (item.items.length === 0) {
      if (item.path) void navigate(item.path)
      return
    }
    const isExpanded = expandedItems.includes(item.id)
    if (isExpanded) {
      setExpandedItems((prev) => prev.filter((id) => id !== item.id))
      return
    }
    setExpandedItems((prev) => [...prev, item.id])
    const destination = getFirstBundleReportPath(item.id)
    if (destination && activeBundleId !== item.id) {
      void navigate(destination)
    }
  }

  const onSubItemClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    subItem: ReportNavSubItem,
  ): void => {
    event.preventDefault()
    event.stopPropagation()
    void navigate(subItem.path)
  }

  return (
    <aside className="reports-side-panel-wrapper" aria-label="Reports">
      <div className="reports-side-panel">
        <div className="reports-side-panel-item" key="reports-title">
          <span className="reports-title">Reports</span>
          <button
            type="button"
            className={`unstyled-button reports-side-panel-icon mgc_list_${
              expandedItems.length ? 'collapse' : 'expansion'
            }_line`}
            onClick={toggleAllItems}
            title={expandedItems.length ? 'Collapse all' : 'Expand all'}
            aria-label={expandedItems.length ? 'Collapse all' : 'Expand all'}
            data-bs-toggle="tooltip"
          />
        </div>
        <div className="reports-side-panel-scrollable">
          {filteredNavItems.length > 0 ? (
            filteredNavItems.map((item) => {
              const normalizedQuery = searchQuery.trim().toLowerCase()
              const itemMatches =
                !!normalizedQuery &&
                (item.id.toLowerCase().includes(normalizedQuery) ||
                  item.label.toLowerCase().includes(normalizedQuery))
              return (
                <div className="reports-side-panel-item-expand" key={item.id}>
                  <button
                    type="button"
                    className={`unstyled-button reports-side-panel-item${
                      isLeafItemActive(item, location.pathname) ? ' active' : ''
                    }`}
                    onClick={(event) => toggleItem(event, item)}
                  >
                    <span>{highlightText(item.label, itemMatches)}</span>
                    {item.items.length > 0 && (
                      <span
                        className={`reports-side-panel-icon mgc_${
                          expandedItems.includes(item.id) ? 'down' : 'right'
                        }_small_fill`}
                        title={
                          expandedItems.includes(item.id)
                            ? 'Collapse'
                            : 'Expand'
                        }
                        data-bs-toggle="tooltip"
                      />
                    )}
                  </button>
                  {expandedItems.includes(item.id) &&
                    item.items.map((subItem) => {
                      const subItemMatches =
                        !!normalizedQuery &&
                        subItem.label.toLowerCase().includes(normalizedQuery)
                      const isActive =
                        activeBundleId === item.id &&
                        activeReportId === subItem.id
                      return (
                        <button
                          type="button"
                          className={`unstyled-button reports-side-panel-sub-item${
                            isActive ? ' active' : ''
                          }`}
                          key={`${item.id}-${subItem.id}`}
                          onClick={(event) => onSubItemClick(event, subItem)}
                        >
                          <span>
                            {highlightText(subItem.label, subItemMatches)}
                          </span>
                        </button>
                      )
                    })}
                </div>
              )
            })
          ) : (
            <EmptyState
              title="No reports found"
              description="Try a different search term."
              illustration={<SearchIcon aria-hidden />}
              className="reports-empty-placeholder"
            />
          )}
        </div>
        <div className="reports-side-panel-footer">
          <span className="reports-side-panel-footer-pill">
            HIPAA Compliant
          </span>
          <span>ENAMELAI ver. 1.0.0</span>
        </div>
      </div>
    </aside>
  )
}

export default ReportsSidebar
